import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import MDBox from "shared/components/MDBox";
import MDButton from "shared/components/MDButton";
import { getRole } from "modules/rol/services/rolService";
import useFormCommerce from "../../../hooks/useFormCommerce";

const FormCommerce = ({
  onSubmit,
  afterSubmit = () => {},
  initialData = {},
  isEditMode = false,
  loading: externalLoading = false,
}) => {
  const formik = useFormCommerce(onSubmit, afterSubmit, initialData, isEditMode);
  const [commerces, setCommerces] = useState([]);
  const [loadingCommerces, setLoadingCommerces] = useState(true);
  const [errorCommerces, setErrorCommerces] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadCommerces = async () => {
      try {
        const commercesData = await getRole();
        if (isMounted) {
          setCommerces(Array.isArray(commercesData) ? commercesData : []);
        }
      } catch (error) {
        if (isMounted) {
          setErrorCommerces("Error al cargar comercios");
          console.error("Error loading roles:", error);
        }
      } finally {
        if (isMounted) {
          setLoadingCommerces(false);
        }
      }
    };

    loadCommerces();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Card>
      <MDBox p={3}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3} direction="column">
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="name"
                label="Nombre del Comercio"
                value={formik.values.name || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                disabled={formik.isSubmitting || externalLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="localization"
                label="Localización del Comercio"
                value={formik.values.localization || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.localization && Boolean(formik.errors.localization)}
                helperText={formik.touched.localization && formik.errors.localization}
                disabled={formik.isSubmitting || externalLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="code"
                label="Codigo del Comercio"
                type="number"
                value={formik.values.code || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.code && Boolean(formik.errors.code)}
                helperText={formik.touched.code && formik.errors.code}
                disabled={formik.isSubmitting || externalLoading}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Estado</InputLabel>
                <Select
                  labelId="status-label"
                  label="Estado"
                  name="status"
                  value={formik.values.status}
                  onChange={(e) => formik.setFieldValue("status", e.target.value)}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting || externalLoading}
                  sx={{ height: 45 }}
                >
                  <MenuItem value={true}>Activo</MenuItem>
                  <MenuItem value={false}>Inactivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={
                  !formik.isValid || formik.isSubmitting || loadingCommerces || externalLoading
                }
              >
                {formik.isSubmitting || externalLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isEditMode ? (
                  "Actualizar Comercio"
                ) : (
                  "Crear Comercio"
                )}
              </MDButton>
            </Grid>
          </Grid>
        </form>
      </MDBox>
    </Card>
  );
};

FormCommerce.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  afterSubmit: PropTypes.func,
  initialData: PropTypes.shape({
    name: PropTypes.string,
    localization: PropTypes.string,
    code: PropTypes.number,
  }),
  isEditMode: PropTypes.bool,
  loading: PropTypes.bool,
};

FormCommerce.defaultProps = {
  afterSubmit: () => {},
  initialData: { status: true },
  isEditMode: false,
  loading: false,
};

export default FormCommerce;
